import { MAX_MAP_ZOOM, MIN_MAP_ZOOM, clampMapZoom } from '@pz/scene';

/**
 * Web-Mercator arithmetic, and the tile grid it implies.
 *
 * Split out of `map.ts` and `tile-map.ts` so there is exactly ONE of it. There
 * were two before — the print renderer's and the widget's pan overlay — and they
 * disagreed about what a fractional zoom meant, so a shopper who dragged the
 * preview by a street got a print file centred half a street away. Everything
 * here is pure and runs under `node --test`; the DOM half lives in `tile-map.ts`.
 */

/** The tile grid these functions lay out. Not 512: see `mapTileUrl`. */
export const TILE_SIZE = 256;

/** Longitude/latitude to Web-Mercator TILE coordinates at a zoom level. */
export function mercator(lat: number, lng: number, zoom: number): { x: number; y: number } {
  const scale = 2 ** zoom;
  const x = ((lng + 180) / 360) * scale;
  const sin = Math.sin((clampLat(lat) * Math.PI) / 180);
  const y = (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * scale;
  return { x, y };
}

/** Web-Mercator tile coordinates back to longitude/latitude. */
export function unmercator(x: number, y: number, zoom: number): { lat: number; lng: number } {
  const scale = 2 ** zoom;
  const lng = (x / scale) * 360 - 180;
  const n = Math.PI - 2 * Math.PI * (y / scale);
  const lat = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
  return { lat, lng };
}

/**
 * Mercator is undefined at the poles — the projection runs to infinity — so the
 * usable band stops just short of them, at the same latitude every web map uses.
 */
export function clampLat(lat: number): number {
  return Math.min(85.05112878, Math.max(-85.05112878, Number.isFinite(lat) ? lat : 0));
}

/** Keeps a longitude in −180…180 by wrapping, so dragging east forever works. */
export function wrapLng(lng: number): number {
  if (!Number.isFinite(lng)) return 0;
  // Short-circuited on the common path: the modulo below is exact arithmetic on
  // paper and lossy in binary, and 20.4489 coming back as 20.44889999999998 is
  // a coordinate that prints one digit wrong on a poster.
  if (lng >= -180 && lng <= 180) return lng;
  let out = ((lng + 180) % 360 + 360) % 360 - 180;
  // −180 and 180 are the same meridian; prefer the positive spelling for stability.
  if (out === -180) out = 180;
  return out;
}

export interface TileView {
  lat: number;
  lng: number;
  /** Fractional, clamped to the levels Mapbox serves. */
  zoom: number;
}

export interface TilePlacement {
  z: number;
  x: number;
  y: number;
  /** Where the tile's top-left corner lands in the viewport, in CSS pixels. */
  dx: number;
  dy: number;
  /** Side length to draw it at, in CSS pixels; not 256 at a fractional zoom. */
  size: number;
}

export interface TileGrid {
  /** The whole zoom level the tiles come from. */
  fetchZoom: number;
  tiles: TilePlacement[];
}

/**
 * The tiles that cover a viewport, and where each one lands.
 *
 * Tiles only exist at whole zooms, so a nearby whole level is fetched and the
 * grid is magnified by the remainder — that is what makes 14.87 a drawable
 * framing rather than something to round away.
 *
 * `dpr` raises the level FETCHED, not the geography: a retina screen gets
 * sharper tiles over exactly the same ground, which is the same rule the print
 * renderer follows for its render scale.
 */
export function tileGrid(
  view: TileView,
  width: number,
  height: number,
  dpr = 1,
): TileGrid {
  // Not clamped: a dialog drawing a design's frame can sit a level or two past
  // the served range (a 250-pixel Customily frame in a 500-pixel dialog is one
  // level deeper). The FETCH is clamped below; the ground is magnified instead.
  const zoom = Number.isFinite(view.zoom) ? view.zoom : MIN_MAP_ZOOM;
  // Rounded, where the print renderer floors: a print gets its resolution from
  // the output size and can afford to magnify, but an interactive map at zoom
  // 13.9 would be showing z13 tiles blown up 1.9x — visibly soft while the
  // shopper is deciding where their town sits.
  const fetchZoom = Math.min(
    MAX_MAP_ZOOM,
    Math.max(MIN_MAP_ZOOM, Math.round(zoom + Math.log2(Math.max(dpr, 1e-6)))),
  );
  const size = TILE_SIZE * 2 ** (zoom - fetchZoom);
  const centre = mercator(view.lat, view.lng, fetchZoom);
  const left = centre.x * size - width / 2;
  const top = centre.y * size - height / 2;

  const firstX = Math.floor(left / size);
  const firstY = Math.floor(top / size);
  const lastX = Math.floor((left + width) / size);
  const lastY = Math.floor((top + height) / size);
  const max = 2 ** fetchZoom;

  const tiles: TilePlacement[] = [];
  for (let tx = firstX; tx <= lastX; tx++) {
    for (let ty = firstY; ty <= lastY; ty++) {
      // North and south of the world there is nothing to draw; east and west it
      // wraps, because the map is a cylinder and dragging past the date line is
      // something people do.
      if (ty < 0 || ty >= max) continue;
      tiles.push({
        z: fetchZoom,
        x: ((tx % max) + max) % max,
        y: ty,
        dx: tx * size - left,
        dy: ty * size - top,
        size,
      });
    }
  }
  return { fetchZoom, tiles };
}

/**
 * The view after dragging the map by a number of screen pixels.
 *
 * Dragging RIGHT moves the world right, so the centre moves LEFT — the sign is
 * the one thing to get wrong here, and getting it wrong makes the map feel
 * broken rather than merely offset.
 */
export function panView(view: TileView, dxPx: number, dyPx: number): TileView {
  const zoom = clampMapZoom(view.zoom);
  const size = TILE_SIZE;
  const centre = mercator(view.lat, view.lng, zoom);
  const moved = unmercator(centre.x - dxPx / size, centre.y - dyPx / size, zoom);
  return { lat: clampLat(moved.lat), lng: wrapLng(moved.lng), zoom };
}

/**
 * The view after zooming by `delta` levels about a point in the viewport.
 *
 * Zooming about the cursor rather than the centre is what makes a wheel feel
 * like a map instead of a slider: the place under the pointer must stay under
 * the pointer. `anchor` is in CSS pixels from the viewport's top-left; passing
 * the centre gives a plain centre zoom.
 */
export function zoomView(
  view: TileView,
  delta: number,
  anchor: { x: number; y: number },
  width: number,
  height: number,
): TileView {
  const from = clampMapZoom(view.zoom);
  const to = clampMapZoom(from + delta);
  if (to === from) return { ...view, zoom: from };

  // The offset from the viewport centre to the anchor, in tile units at each
  // zoom. The ground under the anchor is fixed; the centre moves to keep it there.
  const offX = (anchor.x - width / 2) / TILE_SIZE;
  const offY = (anchor.y - height / 2) / TILE_SIZE;
  const centreFrom = mercator(view.lat, view.lng, from);
  const ground = { x: centreFrom.x + offX, y: centreFrom.y + offY };
  const scale = 2 ** (to - from);
  const centreTo = { x: ground.x * scale - offX, y: ground.y * scale - offY };
  const moved = unmercator(centreTo.x, centreTo.y, to);
  return { lat: clampLat(moved.lat), lng: wrapLng(moved.lng), zoom: to };
}

/**
 * Wheel notches to zoom levels.
 *
 * Browsers report wheel deltas in three different units and the same flick of
 * the same finger arrives as 3, 100 or 1 depending on the device — so the
 * MAGNITUDE is thrown away and only the direction is kept, with a step small
 * enough that a trackpad feels smooth and large enough that a mouse wheel gets
 * somewhere.
 */
export function wheelZoomDelta(deltaY: number, deltaMode = 0): number {
  if (!Number.isFinite(deltaY) || deltaY === 0) return 0;
  const step = deltaMode === 0 ? 0.35 : 1;
  return deltaY < 0 ? step : -step;
}
